import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  projectsApi,
  ProjectCreateInput,
  ProjectDocumentsResponse,
} from "@/services/projectsApi";

export function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: projectsApi.list,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ["projects", id],
    queryFn: () => projectsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (project: ProjectCreateInput) => projectsApi.create(project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof projectsApi.update>[1]) =>
      projectsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", id] });
    },
  });
}

export function useInsertProjectDocuments() {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectDocumentsResponse,
    Error,
    { projectId: string; documentIds: string[] }
  >({
    mutationFn: ({ projectId, documentIds }) =>
      projectsApi.insertDocuments(projectId, documentIds),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] });
    },
  });
}

export function useDeleteProjectDocuments() {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectDocumentsResponse,
    Error,
    { projectId: string; documentIds: string[] }
  >({
    mutationFn: ({ projectId, documentIds }) =>
      projectsApi.deleteDocuments(projectId, documentIds),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] });
    },
  });
}

export function useProjectTemplate(projectId: string | undefined, formatType?: string) {
  return useQuery({
    queryKey: ["projects", projectId, "template", formatType ?? null],
    queryFn: () => projectsApi.getProjectTemplate(projectId!, formatType),
    enabled: !!projectId,
    retry: false,
  });
}

export function useProjectDocumentIds(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "documents"],
    queryFn: () => {
      if (!projectId) return Promise.resolve([]);
      return projectsApi.getDocumentIds(projectId);
    },
    enabled: !!projectId,
    retry: false,
  });
}

export function useTemplateDocuments(
  projectId: string | undefined,
  templateId: string | undefined
) {
  return useQuery({
    queryKey: ["projects", projectId, "templates", templateId, "documents"],
    queryFn: () => {
      if (!projectId || !templateId) return Promise.resolve([]);
      return projectsApi.getTemplateDocuments(projectId, templateId, true);
    },
    enabled: !!projectId && !!templateId,
    retry: false,
  });
}

export function useCreateTemplateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      templateId,
      documentId,
      data,
      status,
    }: {
      projectId: string;
      templateId: string;
      documentId: string;
      data: string | null;
      status?: string;
    }) => projectsApi.createTemplateDocument(projectId, templateId, documentId, data, status),
    onSuccess: (_, { projectId, templateId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "templates", templateId, "documents"],
      });
    },
  });
}

export function useUpdateTemplateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      templateId,
      documentDataId,
      data,
      status,
    }: {
      projectId: string;
      templateId: string;
      documentDataId: string;
      data?: string | null;
      status?: string;
    }) => projectsApi.updateTemplateDocument(projectId, templateId, documentDataId, data || null, status),
    onSuccess: (_, { projectId, templateId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "templates", templateId, "documents"],
      });
    },
  });
}

export function useDeleteTemplateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      templateId,
      documentDataId,
    }: {
      projectId: string;
      templateId: string;
      documentDataId: string;
    }) => projectsApi.deleteTemplateDocument(projectId, templateId, documentDataId),
    onSuccess: (_, { projectId, templateId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "templates", templateId, "documents"],
      });
    },
  });
}

export function useTemplateUsageCount(templateId: string | undefined) {
  return useQuery({
    queryKey: ["projects", "usage-count", "template", templateId],
    queryFn: () => projectsApi.getTemplateUsageCount(templateId!),
    enabled: !!templateId,
    retry: false,
  });
}

export function usePromptUsageCount(promptLibraryId: string | undefined) {
  return useQuery({
    queryKey: ["projects", "usage-count", "prompt-library", promptLibraryId],
    queryFn: () => projectsApi.getPromptUsageCount(promptLibraryId!),
    enabled: !!promptLibraryId,
    retry: false,
  });
}
